import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "https://images.unsplash.com/photo-1733322991133-2de13aa12c57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwc2hvdyUyMHJlZCUyMGNhcnBldCUyMGV2ZW50fGVufDB8fHx8MTc4NzE5OTM0OXww&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const ballDate = new Date("2026-12-19T00:00:00");
  const daysLeft = Math.max(0, Math.ceil((ballDate - new Date()) / 86400000));

  const go = (href) => (e) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Red carpet fashion show" className="h-[120%] w-full object-cover object-center opacity-35 saturate-[0.5]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EFD89A] via-[#EFD89A]/25 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]"
          data-testid="hero-overline"
        >
          08.19 — 12.19 · The Road to the Kindness Ball
        </motion.p>

        <h1 className="font-serif leading-[0.95] text-[#33240E]" data-testid="hero-heading">
          <MaskedLine delay={0.4} className="text-5xl sm:text-7xl lg:text-[7rem] font-black">Kindness is</MaskedLine>
          <MaskedLine delay={0.55} className="text-5xl sm:text-7xl lg:text-[7rem] font-black italic text-[#FFF7E0]">the new</MaskedLine>
          <MaskedLine delay={0.7} className="text-5xl sm:text-7xl lg:text-[7rem] font-black">black-tie<span className="text-velvet">.</span></MaskedLine>
        </h1>

        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-base font-light leading-relaxed text-muted-foreground lg:col-span-5"
            data-testid="hero-subcopy"
          >
            Five months. One activation a day. 1,000 bears finding homes, one million pledges rising —
            and it all ends on a red carpet. The Kindness Ball, December 19, 2026. Earn your way in.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex flex-wrap items-end gap-4 lg:col-span-7 lg:justify-end"
          >
            <a href="#calendar" onClick={go("#calendar")} data-testid="hero-cta-calendar"
              className="bg-[#33240E] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]">
              Today's Activation
            </a>
            <a href="#bears" onClick={go("#bears")} data-testid="hero-cta-bears"
              className="border border-[#33240E]/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
              Adopt a Bear
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-[#33240E]/15 pt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground"
        >
          <span data-testid="hero-countdown"><span className="text-[#8A5A0F]">{daysLeft}</span> days until the Ball</span>
          <span><span className="text-[#8A5A0F]">121</span> daily activations</span>
          <span><span className="text-[#8A5A0F]">1,000</span> bears to adopt</span>
          <span><span className="text-[#8A5A0F]">1,000,000</span> pledges to gather</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
